import {
  Autocomplete,
  Box,
  InputLabel,
  TextField,
  Tooltip,
} from '@mui/material';
import { useUrlHash } from '@client/router/useUrlHash';
import { useQuery } from '@tanstack/react-query';
import { useGetCrmDataQuery } from 'generated/graphql';
import React from 'react';

import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';

import { CrmSegmentSelect } from './components/CrmSegmentSelect';
import { SearchBox } from './components/SearchBox';
import { getLeadsSources } from './CreatorCrm.api';
import { useCrmStore } from './CreatorCrm.store';
import { ExportButton } from './CrmExportButton';
import { CrmSegment } from './CrmSegment.enum';
import { SourceClassification } from './source-classification.enum';
import { FilterOption } from './filter-option';
import { orderBy } from 'lodash';
import { sprinkles } from '@design-system/sprinkles.css';

type SelectProps = {
  value: FilterOption | FilterOption[] | null;
  onChange: (value: FilterOption[]) => void | ((value: FilterOption) => void);
  options: FilterOption[];
  multiple?: boolean;
  width?: number;
};
function Select({
  value,
  onChange,
  options,
  multiple = false,
  width = 250,
}: SelectProps) {
  return (
    <Autocomplete<FilterOption, boolean>
      multiple={multiple}
      value={value}
      onChange={(e, v) => onChange(v as FilterOption[])}
      sx={{ width }}
      options={options}
      renderInput={(params) => <TextField {...params} size="small" />}
      renderOption={(props, option: FilterOption) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps}>
            <Tooltip title={option.label}>
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {option.label}
              </Box>
            </Tooltip>
            {option.tag && (
              <Box
                className={sprinkles({
                  color: 'D-100',
                  fontWeight: 'medium',
                })}
                sx={{ flexShrink: 0, ml: 1 }}
              >
                {option.tag}
              </Box>
            )}
          </li>
        );
      }}
    />
  );
}

const getProductTag = (productType?: string | null): string | undefined => {
  if (productType === 'otp-bundle') {
    return 'OFFER';
  }

  if (productType === 'subscription') {
    return undefined;
  }

  return 'PROGRAM';
};

const selectValue = (
  options: FilterOption[],
  value: string | string[],
  multiple = false,
) => {
  if (multiple) {
    return options.filter((o) => value.includes(String(o.id)));
  }

  return options.find((o) => o?.id === value) ?? null;
};

export function SelectSubscriptionStatus() {
  const crmStore = useCrmStore();

  const subscriptionOptions = [
    { id: 'Cancelled', label: 'Cancelled' },
    { id: 'Active', label: 'Active' },
    { id: 'Inactive', label: 'Inactive' },
    { id: 'BillingFailed', label: 'Billing Failure' },
    crmStore.segment === CrmSegment.ExclusiveBuyers && {
      id: 'None',
      label: 'None',
    },
  ].filter((x) => x) as FilterOption[];

  return (
    <InputLabel>
      <b>Subscription</b>
      <Select
        options={subscriptionOptions}
        value={selectValue(
          subscriptionOptions,
          crmStore.filters.subscription,
          true,
        )}
        onChange={(options: FilterOption[]) =>
          crmStore.handleFilterChange(
            'subscription',
            options.map((option) => option.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectClassification() {
  const crmStore = useCrmStore();

  const classificationOptions = [
    { id: SourceClassification.MigratedSubscription, label: 'Migrated' },
    { id: SourceClassification.FreeSubscription, label: 'Free' },
    { id: SourceClassification.PaidSubscription, label: 'Paid' },
    { id: SourceClassification.TrialSubscription, label: 'Trial' },
    { id: SourceClassification.BulkUpdateSubscription, label: 'Bulk Uploads' },
  ];

  return (
    <InputLabel>
      <b>Classification</b>
      <Select
        options={classificationOptions}
        value={selectValue(
          classificationOptions,
          crmStore.filters.classification,
          true,
        )}
        onChange={(options: FilterOption[]) =>
          crmStore.handleFilterChange(
            'classification',
            options.map((option) => option.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectProduct() {
  const crmStore = useCrmStore();
  const creatorId = useInfluencerId();
  const result = useGetCrmDataQuery({ variables: { creatorId } });

  const products = orderBy(
    result.data?.creator?.subscribedProducts?.map((product) => ({
      id: product,
      label: product,
    })) ?? [],
    'label',
    'asc',
  );

  return (
    <InputLabel>
      <b>Product</b>
      <Select
        options={products}
        value={selectValue(products, crmStore.filters.product, true)}
        onChange={(options: FilterOption[]) =>
          crmStore.handleFilterChange(
            'product',
            options.map((p) => p.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectAdditionalLabel() {
  const crmStore = useCrmStore();

  const additionalLabels = [
    { id: 'Recently converted', label: 'Recently converted' },
    { id: 'Recently churned', label: 'Recently churned' },
    { id: 'First purchase', label: 'First purchase' },
    { id: 'Converted from Free Offer', label: 'Converted from Free Offer' },
  ];

  return (
    <InputLabel>
      <b>Additional</b>
      <Select
        options={additionalLabels}
        value={selectValue(additionalLabels, crmStore.filters.additional, true)}
        onChange={(options: FilterOption[]) =>
          crmStore.handleFilterChange(
            'additional',
            options.map((option) => option.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectOTPs() {
  const crmStore = useCrmStore();
  const creatorId = useInfluencerId();
  const result = useGetCrmDataQuery({
    variables: {
      creatorId,
    },
  });

  const otpOptions = orderBy(
    result.data?.creator?.purchasedOtpProducts?.map((product) => ({
      id: `${product.id}:${product.productType}`,
      label: product.name as string,
      tag: getProductTag(product.productType),
    })) ?? [],
    'label',
    'asc',
  );

  return (
    <InputLabel>
      <b>OTP</b>
      <Select
        options={otpOptions}
        value={selectValue(otpOptions, crmStore.filters.otpOffers, true)}
        onChange={(options: FilterOption[]) =>
          crmStore.handleFilterChange(
            'otpOffers',
            options.map((option) => option.id),
          )
        }
        width={300}
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectFreeOffers() {
  const crmStore = useCrmStore();
  const creatorId = useInfluencerId();
  const result = useGetCrmDataQuery({
    variables: {
      creatorId,
    },
  });

  const freeOfferOptions: FilterOption[] =
    result.data?.creator?.freeOfferProducts?.map((product) => ({
      id: `${product.id}:${product.productType}`,
      label: product.name as string,
      tag: getProductTag(product.productType),
    })) ?? [];

  const sortedOptions = orderBy(freeOfferOptions, 'label', 'asc');

  return (
    <InputLabel>
      <b>Free offer</b>
      <Select
        options={sortedOptions}
        value={selectValue(sortedOptions, crmStore.filters.freeOffers, true)}
        onChange={(options: FilterOption[]) =>
          crmStore.handleFilterChange(
            'freeOffers',
            options.map((option) => option.id),
          )
        }
        width={300}
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectConvertedTo() {
  const crmStore = useCrmStore();
  const creatorId = useInfluencerId();
  const result = useGetCrmDataQuery({
    variables: {
      creatorId,
    },
  });
  const convertedToOptions = orderBy(
    result.data?.creator?.convertedToProducts?.map((product) => ({
      id: `${product.name}:${product.productType}`,
      label: product.name as string,
      tag: getProductTag(product.productType),
    })) ?? [],
    'label',
    'asc',
  );

  return (
    <InputLabel>
      <b>Converted to</b>
      <Select
        options={convertedToOptions}
        value={selectValue(
          convertedToOptions,
          crmStore.filters.convertedTo,
          true,
        )}
        onChange={(options: FilterOption[]) =>
          crmStore.handleFilterChange(
            'convertedTo',
            options.map((option) => option.id),
          )
        }
        width={300}
        multiple={true}
      />
    </InputLabel>
  );
}

export function SelectConversionStatus() {
  const crmStore = useCrmStore();

  const conversionStatusOptions: FilterOption[] = [
    { id: 'true', label: 'Converted' },
    { id: 'false', label: 'Not Converted' },
  ];

  return (
    <InputLabel>
      <b>Conversion status</b>
      <Select
        options={conversionStatusOptions}
        value={selectValue(
          conversionStatusOptions,
          crmStore.filters.conversionStatus[0] ?? '',
        )}
        onChange={(option: FilterOption[]) =>
          crmStore.handleFilterChange(
            'conversionStatus',
            option ? [(option as unknown as FilterOption).id] : [],
          )
        }
      />
    </InputLabel>
  );
}

export function SelectLeadsSource() {
  const crmStore = useCrmStore();
  const { data } = useQuery({
    queryKey: ['crm', 'leadsSources'],
    queryFn: getLeadsSources,
  });

  const leadSources = data ?? [];

  const leadsSourcesOptions: FilterOption[] = [];

  leadSources.forEach((ls) =>
    leadsSourcesOptions.push({
      id: ls.value,
      label: ls.title,
    }),
  );

  return (
    <InputLabel>
      <b>Source</b>
      <Select
        width={200}
        options={leadsSourcesOptions}
        value={selectValue(
          leadsSourcesOptions,
          crmStore.filters.leadsSource,
          true,
        )}
        onChange={(options: FilterOption[]) =>
          crmStore.handleFilterChange(
            'leadsSource',
            options.map((option) => option.id),
          )
        }
        multiple={true}
      />
    </InputLabel>
  );
}

type FiltersBarProps = {
  totalResults: number;
  isFetching?: boolean;
  children: React.ReactNode;
};
export function FiltersBar({
  children,
  totalResults,
  isFetching,
}: FiltersBarProps) {
  const crmStore = useCrmStore();
  const [, setSegmentHash] = useUrlHash();

  const allowedSegments = [
    CrmSegment.Subscribed,
    CrmSegment.ExclusiveBuyers,
    CrmSegment.FreeOffers,
    CrmSegment.Leads,
    CrmSegment.AbandonedCarts,
  ];

  return (
    <div className="flex flex-wrap gap-5 md:w-full">
      <section>
        <InputLabel>
          <b>Segments</b>
          <CrmSegmentSelect
            activeFilter={crmStore.segment}
            setActiveFilter={(segment) => setSegmentHash(segment ?? '')}
            allowedSegments={allowedSegments}
          />
        </InputLabel>
      </section>
      <section className="w-full md:w-auto flex flex-col md:flex-row gap-4">
        {children}
      </section>
      <section className="w-full md:w-auto flex items-start gap-4 justify-between">
        <div className="w-full md:w-auto min-w-[200px]">
          <InputLabel>
            <span>
              <b>Search</b>
              <span
                className="ml-2 text-[#8A8E99]"
                style={{
                  opacity: isFetching ? 0.5 : 1,
                  transition: 'opacity 150ms ease',
                }}
              >
                {totalResults} Results
              </span>
            </span>
            <SearchBox
              onChange={crmStore.setSearchTerm}
              value={crmStore.searchTerm}
            />
          </InputLabel>
        </div>

        <ExportButton />
      </section>
    </div>
  );
}
